import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type ShouldBlockGetter = (() => boolean) | null;

type BackBlockerContextValue = {
  registerBlocker: (getter: ShouldBlockGetter) => void;
  shouldBlockBack: () => boolean;
};

const BackBlockerContext = createContext<
  BackBlockerContextValue | undefined
>(undefined);

export const BackBlockerProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [getter, setGetter] = useState<ShouldBlockGetter>(null);

  const registerBlocker = useCallback((getter: ShouldBlockGetter) => {
    setGetter(() => getter);
  }, []);

  const shouldBlockBack = useCallback(() => {
    return getter ? getter() : false;
  }, [getter]);

  const value = useMemo(
    () => ({ registerBlocker, shouldBlockBack }),
    [registerBlocker, shouldBlockBack]
  );

  return (
    <BackBlockerContext.Provider value={value}>
      {children}
    </BackBlockerContext.Provider>
  );
};

export const useBackBlocker = (shouldBlock: boolean | (() => boolean)) => {
  const ctx = useContext(BackBlockerContext);
  if (!ctx) {
    throw new Error("useBackBlocker must be used within BackBlockerProvider");
  }

  const getterRef = React.useRef(shouldBlock);
  getterRef.current = shouldBlock;

  const stableGetter = React.useCallback(() => {
    const v = getterRef.current;
    return typeof v === "function" ? v() : v;
  }, []);

  React.useEffect(() => {
    ctx.registerBlocker(stableGetter);
    return () => ctx.registerBlocker(null);
  }, [ctx, stableGetter]);
};

export const useBackBlockerContext = (): BackBlockerContextValue | undefined => {
  return useContext(BackBlockerContext);
};
